import axios from "axios"
import { getSession, clearSession, getLanguage } from "../auth/storage"

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

// Identifies this caller to the backend; no server branch keys off it today.
const PLATFORM = "pwa"

const client = axios.create({ baseURL: API_BASE_URL, timeout: 30000 })

client.interceptors.request.use((config) => {
  const session = getSession()

  config.headers["Content-Type"] = "application/json"
  config.headers["X-Platform"] = PLATFORM
  config.headers["time-zone"] = Intl.DateTimeFormat().resolvedOptions().timeZone

  // A request pinned to a language or restaurant (a sync, a queued flag) keeps its own.
  if (!hasHeader(config.headers, "X-Lang")) config.headers["X-Lang"] = getLanguage()
  if (session?.tokens?.access) {
    config.headers.Authorization = `Bearer ${session.tokens.access}`
  }
  if (session?.restaurant?.id && !hasHeader(config.headers, "restaurant-id")) {
    config.headers["restaurant-id"] = session.restaurant.id
  }
  return config
})

function hasHeader(headers, name) {
  if (typeof headers.has === "function") return headers.has(name)
  return Object.keys(headers).some((key) => key.toLowerCase() === name.toLowerCase())
}

// Handlers registered by the session provider so a dead token routes back to login.
let onUnauthorized = null

export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler
}

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status
    if (status === 401) {
      clearSession()
      onUnauthorized?.()
    }
    return Promise.reject(new ApiError(error))
  }
)

// Every success response is wrapped as { success, code, data } by the API renderer.
export function unwrap(response) {
  return response?.data?.data
}

export class ApiError extends Error {
  constructor(error) {
    super(readErrorMessage(error))
    this.name = "ApiError"
    this.status = error.response?.status ?? null
    this.cause = error
  }
}

// Cancelled, or timed out after the request may already have reached the server.
const NOT_OFFLINE_CODES = new Set(["ERR_CANCELED", "ECONNABORTED", "ETIMEDOUT"])

// No response because there was no connection, as opposed to the server refusing or a timeout.
export function isNetworkError(error) {
  return error instanceof ApiError && error.status === null && !NOT_OFFLINE_CODES.has(error.cause?.code)
}

// Errors come back as { code, error: { message: [...] } }.
function readErrorMessage(error) {
  const message = error.response?.data?.error?.message
  if (Array.isArray(message) && message.length) return String(message[0])
  if (typeof message === "string" && message) return message
  if (error.code === "ECONNABORTED") return "The request timed out. Please try again."
  if (!error.response) return "You appear to be offline. Check your connection."
  return "Something went wrong. Please try again."
}

export default client
